import services from 'services/module';
import {Strings} from 'Strings';
import {Utils} from '../underscore-extensions';
import {LocalStorageSvc} from './LocalStorageSvc';
import {LandingTemplateSvc} from '../services/landingTemplateSvc';
import * as visitControl from './visitControl';

export const clearVisitStore = visitControl.clear;

export class AnalyticSvc {
    private mixpanelEnabled = true;
    private activateKeyName = 'analytic_track_activate_page_';

    constructor(private $location: ng.ILocationService,
                private $state: ng.ui.IStateService,
                private $rootScope: ng.IRootScopeService,
                private $log: ng.ILogService,
                private landingTemplateSvc: LandingTemplateSvc,
                private ipCookie: ipCookie,
                private LocalStorageSvc: LocalStorageSvc) {
        $rootScope.$on('$stateChangeSuccess', () => {
            this.registerCommonProperties();
            this.trackVisit();
        });
    }

    getGaHeader = () => ({
        'anm-mkt-ga': this.ipCookie('_ga')
    });

    upgradeShown(action: anm.UpgradeAction, plan = this.getDefaultPlan()) {
        const opt = {
            [`${this.getProduct()} Plan`]: plan
        };

        this.registerUserProperties();
        this.trackMixpanel(`${Strings.capitalize(action)} Shown`, opt);
    }

    changeBillingPeriod(oldPeriod: string, newPeriod: string) {
        const opt = {
            ...this.getPlan(),
            'Old Period': oldPeriod,
            'New Period': newPeriod
        };

        this.registerUserProperties();
        this.trackMixpanel('Change Billing Period', opt);
    }

    changeSubscription(action: anm.UpgradeAction,
                       oldPlan: string,
                       oldPeriod: string,
                       toPlan = this.getDefaultPlan(),
                       toPeriod?: string,
                       chargedAmount?: number,
                       coupon?: string) {
        // this.gaChangeSubscription(action, toPlan);

        this.mpChangeSubscription(
            action,
            oldPlan,
            oldPeriod,
            toPlan,
            toPeriod,
            chargedAmount,
            coupon
        );

        this.fbqChangeSubscription(
            oldPlan,
            toPlan,
            toPeriod
        )
    }

    addTransaction(price: string, subscriptionName: string) {
        const transactionId = `${options.user.userId} - ${Date.now()}`;
        this.gaAddTransaction(transactionId, price, subscriptionName);
        this.fbqAddTransaction(transactionId, price, subscriptionName);
    }

    private gaAddTransaction(
        id: string,
        price: string,
        name: string
    ) {
        this.ga('ecommerce:addTransaction', {
            id,
            revenue: price
        });

        this.ga('ecommerce:addItem', {
            id,
            name,
            price,
            quantity: 1
        });

        this.ga('ecommerce:send');
    }

    private fbqAddTransaction(id: string,
                              price: string,
                              planName: string) {
        this.fbq('track', 'Purchase', {
            id,
            planName,
            value: price,
            currency: 'USD'
        });
    }

    signUpSubmit() {
        // this.gaSignUpEvent('Submit');
        // this.gaProductSignUpEvent();
        this.getMixPanel(mixpanel => {
            this.registerUserProperties();
            mixpanel.track('Sign Up Submit', {Provider: 'EMAIL'});
        });
    }

    signUpActivate(hash: string) {
        const currentHash = this.LocalStorageSvc.get(this.activateKeyName);

        if (hash && hash !== currentHash) {
            // this.gaSignUpEvent('Activate');
            this.getMixPanel(mixpanel => {
                this.registerUserProperties();
                mixpanel.track('Sign Up Activation', {Provider: 'EMAIL'});
            });
            this.LocalStorageSvc.set(this.activateKeyName, hash);
        }
    }

    signUpComplete() {
        // this.gaSignUpEvent('Complete');
        this.fbPixelSignUpComplete();
        this.trackSignUp('Email');
    }

    signUpProfileComplete(provider: anm.SignUpProvider) {
        // this.gaSignUpEvent('ProfileComplete', provider);
    }

    signUpSocialSubmit(provider: anm.SignUpProvider) {
        // this.gaSignUpEvent('Submit', provider);
    }

    signUpSocialComplete(provider: anm.SignUpProvider) {
        // this.gaSignUpEvent('Complete', provider);
        this.fbPixelSignUpComplete();
        this.trackSignUp(provider);
    }

    login(user: anm.User) {
        // this.gaSetUserId(user.userId); // TODO maybe need in the future
        this.trackLoginMixpanel();
    }

    logout() {
        // this.gaSetUserId(null);// TODO maybe need in the future
        this.mixpanelLogout();
    }

    visitPage(pageName: string, additionalParams = {}) {
        this.registerCommonProperties();
        const params = {Product: this.getProduct(), ...additionalParams};
        this.trackMixpanel(`Visit ${pageName} Page`, params);
    }

    changePage() {
        if (!window.ga) return;

        this.ga('set', 'page', this.$location.url());
        this.ga('send', 'pageview');
    }

    getPlan() {
        const {wave, studio} = options.user.subscriptionDetails;
        return {
            'WAVE Plan': wave.displayName,
            'STUDIO Plan': studio.displayName
        }
    }

    selectProduct(product: AnimatronProduct) {
        this.mpSelectProduct(product);
    }

    private fbq(...arg: any[]) {
        return window.fbq && window.fbq(...arg);
    }

    private ga(...arg: any[]) {
        return window.ga && window.ga(...arg);
    }

    private mpSelectProduct(product: AnimatronProduct) {
        return this.getMixPanel(mixpanel => {
            mixpanel.track('Selected Product', {'Product': product});
        });
    }

    private fbPixelSignUpComplete() {
        const {
            industry,
            goals,
            occupation
        } = options.user.meta;
        const params = {
            product: this.getProduct(),
            industry: industry,
            goals: goals
        };

        if (occupation) {
            params['occupation'] = occupation
        }

        this.fbq('trackCustom', 'CompleteRegistration', params);
    }

    private fbqChangeSubscription(oldPlan: string,
                                  toPlan = this.getDefaultPlan(),
                                  toPeriod?: string) {
        this.fbq('trackCustom', 'ChangePlan', {
            product: this.getProduct(),
            new_plan: toPlan,
            old_plan: oldPlan,
            period: toPeriod
        });
    }

    private mpChangeSubscription(action: anm.UpgradeAction,
                                 oldPlan: string,
                                 oldPeriod: string,
                                 toPlan = this.getDefaultPlan(),
                                 toPeriod?: string,
                                 chargedAmount?: number,
                                 coupon?: string) {
        this.getMixPanel(mixpanel => {
            const params = {
                [`${this.getProduct()} Plan`]: toPlan,
                [`${this.getProduct()} Old Plan`]: oldPlan,
                'Old Period': oldPeriod,
                Period: toPeriod
            };

            if (chargedAmount && action === 'upgrade') {
                params['Charged'] = chargedAmount.toString();
                mixpanel.people.track_charge(chargedAmount);
            }

            if (coupon) params['Coupon'] = coupon;

            const eventName = Strings.capitalize(action) + 'd';
            this.registerUserProperties();
            this.trackMixpanel(eventName, params);
        });
    }

    private gaChangeSubscription(action: anm.UpgradeAction,
                                 toPlan: string) {
        const isFree = ['WaveFree', 'Free'].includes(toPlan);
        if (action === 'upgrade' || isFree) {
            const eventName = Strings.capitalize(action);
            this.ga('send', 'event', this.getProduct(), eventName);
        }
    }

    private trackVisit() {
        return this.getMixPanel(mixpanel => {
            const userId = options.user && options.user.userId;
            const canVisit =
                !this.$state.current.name.includes('activate') &&
                visitControl.canVisit(userId);

            if (!canVisit) return;

            const params = {Source: this.getSource()};
            mixpanel.register_once(params);
            mixpanel.people.set_once(params);

            if (options.user.anonymous) {
                const first_params = AnalyticSvc.campaignParams();
                const landingInfoParams = this.landingTemplateSvc.getTrackInfo();

                mixpanel.people.set_once(first_params);

                mixpanel.track('Guest Visit', {...landingInfoParams, ...first_params});
            } else {
                mixpanel.identify(options.user.userIdAsString);
                mixpanel.track('Visit', {});
            }

            visitControl.visit(userId);
        });
    }

    private static getQueryParam(url: string, param: string) {
        // Expects a raw URL
        param = param.replace(/[[]/, "\[").replace(/[]]/, "\]");
        const regexS = "[\?&]" + param + "=([^&#]*)",
            regex = new RegExp(regexS),
            results = regex.exec(url);
        if (results === null || (results && typeof(results[1]) !== 'string' && results[1].length)) {
            return '';
        } else {
            return decodeURIComponent(results[1]).replace(/\W/gi, ' ');
        }
    };


    private static campaignParams() {
        let campaign_keywords = 'utm_source utm_medium utm_campaign utm_content utm_term'.split(' ')
            , kw = ''
            , first_params = {};

        let index;
        for (index = 0; index < campaign_keywords.length; ++index) {
            kw = AnalyticSvc.getQueryParam(document.URL, campaign_keywords[index]);
            if (kw.length) {
                first_params[campaign_keywords[index] + ' [first touch]'] = kw;
            }
        }

        return first_params;
    }

    private getSource() {
        const referrer = document.referrer;
        if (referrer.search('https?://(.*)google.([^/?]*)') === 0) {
            return 'Google';
        } else if (referrer.search('https?://(.*)bing.([^/?]*)') === 0) {
            return 'Bing';
        } else if (referrer.search('https?://(.*)quora.([^/?]*)') === 0) {
            return 'Quora';
        } else if (referrer.search('https?://(.*)facebook.([^/?]*)') === 0) {
            return 'Facebook';
        } else if (referrer.search('https?://(.*)twitter.([^/?]*)') === 0) {
            return 'Twitter';
        } else if (referrer.search('https?://(.*)animatron.([^/?]*)') === 0) {
            return 'Animatron';
        } else if (referrer.search('https?://(.*)youtube.([^/?]*)') === 0) {
            return 'Youtube';
        } else if (referrer.search('https?://(.*)yahoo.([^/?]*)') === 0) {
            return 'Yahoo';
        } else if (referrer.search('https?://(.*)naver.([^/?]*)') === 0) {
            return 'Naver';
        } else {
            return referrer;
        }
    }

    private mixpanelLogout() {
        return this.getMixPanel(mixpanel => {
            mixpanel.reset();
        });
    }

    private getMixPanel(func: (m: Mixpanel) => void): void {
        return this.mixpanelEnabled && window.mixpanel && func(window.mixpanel)
    }

    private registerCommonProperties() {
        const props = {
            Place: 'Website'
        };

        if (this.getProduct() !== 'ANY') {
            props['Product'] = this.getProduct();
        }

        return this.getMixPanel(mixpanel => mixpanel.register(props));
    }

    private registerUserProperties() {
        this.getMixPanel(mixpanel => {
            const props = {
                ...this.getPlan(),
                '$name': options.user.displayName,
                Username: options.user.username,
                Product: this.getProduct()
            };

            if (options.user.email) {
                props['$email'] = options.user.email;
            }

            mixpanel.register(props);
        });
    }

    private userInfoMixpanel() {
        const displayName = options.user.displayName,
            idx = displayName.indexOf(' ');

        const props = {
            ...this.getPlan(),
            '$name': displayName,
            '$first_name': idx >= 0 ? displayName.substring(0, idx) : displayName,
            '$last_name': idx >= 0 ? displayName.substring(idx + 1) : '',
            userId: options.user.userIdAsString,
            Username: options.user.username,
            Industry: options.user.meta.industry,
            Occupation: options.user.meta.occupation,
            Goals: options.user.meta.goals
        };

        if (options.user.email) {
            props['$email'] = options.user.email;
        }

        return props;
    }

    private trackMixpanel(eventName: string, opts?: { [index: string]: any }) {
        return this.getMixPanel(m => {
            // the setTimeout fix some problem with browser request canceled
            setTimeout(() => {
                m.track(eventName, opts);
                this.$log.debug('Mixpanel track: ', ...m[0]);
            }, 100);

            return m;
        });
    }

    private trackLoginMixpanel() {
        return this.getMixPanel(mixpanel => {
            this.registerUserProperties();
            this.trackVisit();
            mixpanel.people.set(this.userInfoMixpanel());
        });
    }

    private isAllowedMethod(provider: string) {
        return !_.isUndefined(provider) &&
            ['google', 'facebook', 'email', 'twitter'].indexOf(provider.toLowerCase()) !== 1;
    }

    private trackSignUp(provider: anm.SignUpProvider) {
        if (this.isAllowedMethod(provider)) {
            this.getMixPanel(mixpanel => {
                mixpanel.alias(options.user.userIdAsString);
                mixpanel.people.set(_.extend({
                    '$created': new Date().toISOString(),
                    Provider: provider,
                    Place: 'Website',
                    Product: options.user.meta.product
                }, this.userInfoMixpanel()));
                this.registerUserProperties();
                mixpanel.track('Sign Up', {Provider: Strings.capitalize(provider)});
            });
        }
    }

    private getDefaultPlan() {
        return this.getProduct() === 'WAVE' ? 'WaveFree' : 'Free';
    }

    private getProduct() {
        return Utils.getProduct();
    }

    private gaSetUserId(userId: string) {
        this.ga('set', 'userId', userId);

    }

    private gaSignUpEvent(eventName: string, provider = 'Email') {
        this.ga('send', 'event', 'SignUp', eventName, Strings.capitalize(provider.toLowerCase()));
    }

    private gaProductSignUpEvent() {
        this.ga('send', 'event', this.getProduct(), 'Sign Up');
    }
}

services.service('analyticSvc', AnalyticSvc);



// WEBPACK FOOTER //
// ./app/assets/javascripts/services/AnalyticSvc.ts